import { ApplicationUpsell as RenterInsuranceUpsell } from '@zg-rentals/renters-insurance';
import {
  Accordion,
  Alert,
  Box,
  Heading,
  Icon,
  IconButton,
  Menu,
  Paragraph,
  PropertyCard,
  Tag,
  Text,
} from '@zillow/constellation-10';
import { IconExternalFilled, IconMoreFilled } from '@zillow/constellation-icons';
import { useRouter } from 'next/router';
import { Fragment, useState } from 'react';
import { REPORTS_EXPIRED_TEXT } from '../../constants/applicationsConstants';
import { getApplicationRoute } from '../../constants/urls';
import {
  type AppfolioRentalApplication,
  ApplicationStatus,
  type GetApplicationsForUserQuery,
  type RentalApplication,
  type UserStatus,
} from '../../graphql/queries';
import {
  getCreditAndBackgroundReportExpiration,
  getStartedByTextWithDate,
  getTagAppearance,
  getTagText,
} from '../../utils/applications-list';
import { getCanShareApplication, getIsComplete } from '../../utils/business-logic';
import { getFormattedAddress } from '../../utils/formatters';
import { WithdrawApplication } from '../WithdrawApplication';
import { ApplicationsEmptyState } from './ApplicationsEmptyState';
import { emitWithdrawModalEvent } from '../../analytics/clickstreamEvents';
import { ApplicationSubmitted } from '../ApplicationEdit/ApplicationSubmitted';
import { useBaseContext } from '../../contexts/BaseContext';
import { ApplicationShareUpsell } from './ApplicationShareUpsell';
import { ConditionalApprovalPopper } from '../Poppers/ConditionalApprovalPopper';
import { useApplicationContextOrUndefined } from '../../contexts/ApplicationContext';
import { trackEvent } from '../../analytics';

interface Props {
  data: GetApplicationsForUserQuery;
}

export function ApplicationsList({ data }: Props) {
  const { submittedApplication, setSubmittedApplication } = useBaseContext();

  const [displayUpsell, setDisplayUpsell] = useState(true);
  const rentalApplications = data?.rentalApplications || [];
  const appfolioApplications = data?.appfolioRentalApplications || [];
  const userStatuses = data?.userStatuses ? (data.userStatuses as Array<UserStatus>) : [];
  const reportsExpirationText = getCreditAndBackgroundReportExpiration(userStatuses);

  const sortApplicationsByDate = (
    a: RentalApplication | AppfolioRentalApplication | null,
    b: RentalApplication | AppfolioRentalApplication | null,
  ) => {
    const getDate = (app: RentalApplication | AppfolioRentalApplication | null) => {
      if (!app) return null;
      return app.startedDateTime;
    };

    const dateA = getDate(a);
    const dateB = getDate(b);

    if (!dateA && !dateB) return 0;
    if (!dateA) return 1;
    if (!dateB) return -1;

    return new Date(dateB).getTime() - new Date(dateA).getTime();
  };

  const declinedAppFolioApplications = appfolioApplications.filter(
    (application) => application?.status === ApplicationStatus.declined,
  );
  const activeAppFolioApplications = appfolioApplications.filter(
    (application) => application?.status !== ApplicationStatus.declined,
  );

  const archivedRentalApplications =
    rentalApplications?.filter(
      (application) =>
        application?.isArchived === true || application?.applicationStatus === ApplicationStatus.declined,
    ) || [];
  const activeRentalApplications =
    rentalApplications?.filter((application) => !archivedRentalApplications.includes(application)) || [];

  const activeApplications: Array<RentalApplication | AppfolioRentalApplication> = [
    ...activeRentalApplications.filter(Boolean),
    ...activeAppFolioApplications.filter(Boolean),
  ] as Array<RentalApplication | AppfolioRentalApplication>;
  const archivedApplications: Array<RentalApplication | AppfolioRentalApplication> = [
    ...archivedRentalApplications.filter(Boolean),
    ...declinedAppFolioApplications.filter(Boolean),
  ] as Array<RentalApplication | AppfolioRentalApplication>;

  const canShareApplication = getCanShareApplication(rentalApplications, userStatuses);

  return (
    <>
      {reportsExpirationText && (
        <Alert tone={reportsExpirationText === REPORTS_EXPIRED_TEXT ? 'warning' : 'info'} css={{ marginBottom: 'sm' }}>
          {reportsExpirationText}
        </Alert>
      )}
      {displayUpsell && rentalApplications && rentalApplications.length > 0 && (
        <Box css={{ marginBottom: 'sm' }}>
          <RenterInsuranceUpsell
            listingAlias={rentalApplications[0]?.rentalListing?.listingAlias || ''}
            onCloseUpsell={() => setDisplayUpsell(false)}
            trackClickstreamEvent={trackEvent}
          />
        </Box>
      )}
      {activeApplications.length > 0 && (
        <>
          {canShareApplication && (
            <Box css={{ marginBottom: 'sm' }}>
              <ApplicationShareUpsell userStatuses={userStatuses} />
            </Box>
          )}
          <Heading level={2} textStyle="responsive.heading-xs" css={{ marginBottom: 'sm' }}>
            Applications
          </Heading>
          {activeApplications.sort(sortApplicationsByDate).map((application, index) => (
            <Fragment key={application?.id}>
              <Box css={{ marginBottom: index === activeApplications.length - 1 ? undefined : 'sm' }}>
                {application &&
                  (application.__typename === 'AppfolioRentalApplication' ? (
                    <AppFolioApplicationCard key={application?.id} application={application} />
                  ) : (
                    <ApplicationCard key={application?.id} application={application as RentalApplication} />
                  ))}
              </Box>
            </Fragment>
          ))}
        </>
      )}
      {archivedApplications.length > 0 && (
        <>
          <Accordion.Root
            css={{ marginTop: 'md' }}
            defaultExpanded={activeApplications.length === 0 ? ['archived'] : []}
          >
            <Accordion.Item value="archived">
              <Accordion.Header>
                <Heading level={3} textStyle="responsive.heading-xs">
                  Archived
                </Heading>
              </Accordion.Header>
              <Accordion.Panel>
                <Paragraph css={{ color: 'text.subtle', marginBottom: 'md' }}>
                  Applications shown here were declined, withdrawn, or are over 90 days old. They’ll be deleted after 2
                  years.
                </Paragraph>
                {archivedApplications.sort(sortApplicationsByDate).map((application, index) => (
                  <Fragment key={application?.id}>
                    <Box css={{ marginBottom: index === archivedApplications.length - 1 ? undefined : 'sm' }}>
                      {application &&
                        (application.__typename === 'AppfolioRentalApplication' ? (
                          <AppFolioApplicationCard key={application?.id} application={application} />
                        ) : (
                          <ApplicationCard key={application?.id} application={application as RentalApplication} />
                        ))}
                    </Box>
                  </Fragment>
                ))}
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion.Root>
        </>
      )}
      {activeApplications.length === 0 && archivedApplications.length === 0 && <ApplicationsEmptyState />}
      {submittedApplication && setSubmittedApplication && (
        <ApplicationSubmitted
          onClose={() => setSubmittedApplication(undefined)}
          submittedApplication={submittedApplication}
          userStatuses={userStatuses}
        />
      )}
    </>
  );
}

function ApplicationCard({ application }: { application: RentalApplication }) {
  const router = useRouter();
  const applicationContext = useApplicationContextOrUndefined();
  const { rentalListing, applicationShareInformation } = application;
  const formattedAddress = getFormattedAddress(rentalListing?.address || applicationShareInformation?.propertyAddress);
  const [isWithdrawModalOpen, setIsWithdrawModalOpen] = useState(false);
  const startedByText = getStartedByTextWithDate(application);
  const isCompleted = getIsComplete(application);

  const applicationId = application.id as string;

  return (
    <>
      <PropertyCard
        appearance="horizontal"
        aria-label={`Application for ${formattedAddress}`}
        data={{
          dataArea1: (
            <Box css={{ marginLeft: 'xs', marginBlock: 'xs' }}>
              <ApplicationTag application={application} />
            </Box>
          ),
          dataArea2: (
            <Box css={{ marginLeft: 'xs', marginBottom: 'xs' }}>
              <Heading level={2} textStyle="responsive.body-lg-bold">
                {formattedAddress}
              </Heading>
            </Box>
          ),
          dataArea3: startedByText ? (
            <Box css={{ marginLeft: 'xs', marginBottom: 'xs' }}>
              <Text css={{ color: 'text.subtle' }} textStyle="body-sm">
                {startedByText}
              </Text>
            </Box>
          ) : null,
        }}
        onClick={() => router.push(getApplicationRoute({ applicationId }))}
        photoBody={
          <PropertyCard.Photo
            src={rentalListing?.primaryPhoto?.thumbUrl as string}
            alt={`Photo of ${rentalListing?.address?.street}`}
            fallbackImg={<PropertyCard.PhotoFallback />}
          />
        }
        actionButton={
          <Menu
            trigger={
              <IconButton title="More" emphasis="bare" tone="brand" shape="square" onClick={(e) => e.stopPropagation()}>
                <Icon>
                  <IconMoreFilled />
                </Icon>
              </IconButton>
            }
            content={
              <>
                <Menu.Item
                  onClick={(e) => {
                    e.stopPropagation();
                    router.push(getApplicationRoute({ applicationId }));
                  }}
                >
                  <Menu.ItemLabel>View application</Menu.ItemLabel>
                </Menu.Item>
                {!!rentalListing?.propertyUrl && (
                  <Menu.Item
                    onClick={(e) => {
                      e.stopPropagation();
                      window.location.assign(rentalListing?.propertyUrl as string);
                    }}
                  >
                    <Menu.ItemLabel>View listing</Menu.ItemLabel>
                  </Menu.Item>
                )}
                {isCompleted && (
                  <Menu.Item
                    onClick={(e) => {
                      emitWithdrawModalEvent(applicationContext);
                      e.stopPropagation();
                      setIsWithdrawModalOpen(true);
                    }}
                  >
                    <Menu.ItemLabel>Withdraw application</Menu.ItemLabel>
                  </Menu.Item>
                )}
              </>
            }
          />
        }
        css={{ maxWidth: '100%' }}
      />
      <WithdrawApplication
        application={application}
        isOpen={isWithdrawModalOpen}
        onCloseModal={() => setIsWithdrawModalOpen(false)}
      />
    </>
  );
}

function ApplicationTag({ application }: { application: RentalApplication | AppfolioRentalApplication }) {
  const getAppFolioTagAppearance = (status?: ApplicationStatus | null) => {
    switch (status) {
      case ApplicationStatus.approved:
        return 'success';
      case ApplicationStatus.submitted:
        return 'success';
      case ApplicationStatus.declined:
        return 'info';
      case ApplicationStatus.withdrawn:
        return 'gray';
      default:
        return 'gray';
    }
  };

  const getAppFolioTagText = (status?: ApplicationStatus | null) => {
    switch (status) {
      case ApplicationStatus.approved:
        return 'Approved';
      case ApplicationStatus.submitted:
        return 'Applied';
      case ApplicationStatus.declined:
        return 'Declined';
      case ApplicationStatus.withdrawn:
        return 'Withdrawn';
      default:
        return 'In progress';
    }
  };

  if (application.__typename === 'AppfolioRentalApplication') {
    return (
      <Tag
        tone={getAppFolioTagAppearance(application.status)}
        size="sm"
        css={{
          textTransform: 'uppercase',
        }}
      >
        {getAppFolioTagText(application.status)}
      </Tag>
    );
  }

  return (
    <Box css={{ display: 'flex', alignItems: 'start', gap: 'xs' }}>
      <Tag
        tone={getTagAppearance(application as RentalApplication)}
        size="sm"
        css={{
          textTransform: 'uppercase',
        }}
      >
        {getTagText(application as RentalApplication)}
      </Tag>
      {(application as RentalApplication).applicationStatus === ApplicationStatus.conditionally_approved && (
        <ConditionalApprovalPopper />
      )}
    </Box>
  );
}

function AppFolioApplicationCard({ application }: { application: AppfolioRentalApplication }) {
  const getAppFolioDescription = (application: AppfolioRentalApplication) => {
    const formatDate = (dateString?: string | null) => {
      if (!dateString) return null;
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    };

    if (application.withdrawnDateTime) {
      const date = formatDate(application.withdrawnDateTime);
      return `You withdrew this AppFolio application on ${date}`;
    }

    if (application.declinedDateTime) {
      const date = formatDate(application.declinedDateTime);
      return `Your AppFolio application was declined on ${date}`;
    }

    if (application.approvedDateTime) {
      const date = formatDate(application.approvedDateTime);
      return `Your AppFolio application was approved on ${date}`;
    }

    if (application.submittedDateTime) {
      const date = formatDate(application.submittedDateTime);
      return `You sent this AppFolio application on ${date}`;
    }

    if (application.startedDateTime) {
      const date = formatDate(application.startedDateTime);
      return `You started this AppFolio application on ${date}`;
    }

    return null;
  };

  const getFormattedAddress = (address?: any) => {
    if (!address) return 'Property application';
    if (typeof address === 'string') return address;
    if (address.street) {
      const parts = [address.street];
      if (address.city) parts.push(address.city);
      if (address.state && address.zip) {
        parts.push(`${address.state} ${address.zip}`);
      } else if (address.state) {
        parts.push(address.state);
      } else if (address.zip) {
        parts.push(address.zip);
      }
      return parts.join(', ');
    }
    return 'Property application';
  };

  const formattedAddress = getFormattedAddress(application.address);

  return (
    <PropertyCard
      appearance="horizontal"
      aria-label={`AppFolio application for ${formattedAddress}`}
      data={{
        dataArea1: (
          <Box css={{ marginLeft: 'xs', marginBlock: 'xs' }}>
            <ApplicationTag application={application} />
          </Box>
        ),
        dataArea2: (
          <Box css={{ marginLeft: 'xs', marginBottom: 'xs' }}>
            <Heading level={2} textStyle="responsive.body-lg-bold">
              {formattedAddress}
            </Heading>
          </Box>
        ),
        dataArea3: getAppFolioDescription(application) ? (
          <Box css={{ marginLeft: 'xs', marginBottom: 'xs' }}>
            <Text css={{ color: 'text.subtle' }} textStyle="body-sm">
              {getAppFolioDescription(application)}
            </Text>
          </Box>
        ) : null,
      }}
      onClick={() => {
        if (application.applicationUrl) {
          window.open(application.applicationUrl, '_blank');
        }
      }}
      photoBody={
        <PropertyCard.Photo
          src={application?.displayablePhotoUrl as string}
          alt={`Photo of ${formattedAddress}`}
          fallbackImg={<PropertyCard.PhotoFallback />}
        />
      }
      actionButton={
        <Box css={{ marginRight: 'xs', marginTop: 'xs' }}>
          {application.applicationUrl && (
            <Icon
              css={{
                color: 'icon.action.hero.default',
              }}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                window.open(application.applicationUrl!, '_blank');
              }}
            >
              <IconExternalFilled />
            </Icon>
          )}
        </Box>
      }
      css={{ maxWidth: '100%' }}
    />
  );
}
